import React from "react";
import { motion } from "framer-motion";
import {
  Lock, Activity, Trophy, Shield, Users, Download,
} from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";
import SectionLabel from "./SectionLabel";
import { GateMock, LeagueMock, PhoneFrame } from "@/components/mockups/PhoneMockup";

const ICONS = [Lock, Activity, Trophy, Shield, Users, Download];

export default function Features() {
  const { t } = useI18n();
  const items = t.features.items;

  return (
    <section id="features" data-testid="features-section" className="relative py-20 md:py-32 border-t border-line">
      <div className="container-px mx-auto max-w-7xl">
        <SectionLabel>{t.features.label}</SectionLabel>
        <h2 className="mt-6 font-display font-bold text-ink text-balance leading-[1.05]
                       text-3xl sm:text-5xl lg:text-6xl max-w-2xl">
          {t.features.title}
        </h2>

        {/* Bento grid */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-6 gap-3 md:gap-4">
          {/* Big card 1: Native blocking + Gate mockup */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55 }}
            className="card-tactical relative col-span-1 md:col-span-3 row-span-2 p-6 md:p-8 overflow-hidden"
            data-testid="feature-bento-0"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="max-w-xs">
                <div className="h-10 w-10 grid place-items-center rounded-md hairline mb-5"
                     style={{ background: "rgba(255,59,59,0.08)" }}>
                  <Lock size={18} color="#FF3B3B" />
                </div>
                <h3 className="font-display text-ink text-2xl font-semibold">{items[0].t}</h3>
                <p className="text-ink/60 mt-3 text-sm leading-relaxed">{items[0].d}</p>
                <p className="label-tag mt-6 text-[10px]">SCREEN TIME // DIGITAL WELLBEING</p>
              </div>
              <div className="hidden md:block flex-shrink-0 w-[170px] -mr-4">
                <PhoneFrame glow={false}>
                  <GateMock />
                </PhoneFrame>
              </div>
            </div>
            <div className="absolute -bottom-12 left-0 right-0 h-32 pointer-events-none"
                 style={{ background: "radial-gradient(60% 100% at 30% 100%, rgba(255,59,59,0.10), transparent)" }} />
          </motion.div>

          {/* Anti-cheat */}
          <FeatureCard idx={1} title={items[1].t} desc={items[1].d} Icon={ICONS[1]} accent="#00FF88" colSpan={3}>
            <div className="mt-6 grid grid-cols-3 gap-2">
              {[36, 72, 88, 54, 96, 80, 48].map((h, i) => (
                <div key={i} className="h-20 rounded-md hairline relative overflow-hidden" style={{ background: "rgba(0,255,136,0.04)" }}>
                  <div className="absolute bottom-0 left-0 right-0 bg-primary/70" style={{ height: `${h}%` }} />
                </div>
              )).slice(0,3)}
            </div>
          </FeatureCard>

          {/* Leagues with mockup */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55, delay: 0.05 }}
            className="card-tactical relative col-span-1 md:col-span-3 p-6 md:p-7 overflow-hidden"
            data-testid="feature-bento-2"
          >
            <div className="flex items-start gap-4">
              <div className="h-10 w-10 grid place-items-center rounded-md hairline flex-shrink-0"
                   style={{ background: "rgba(255,184,0,0.08)" }}>
                <Trophy size={18} color="#FFB800" />
              </div>
              <div>
                <h3 className="font-display text-ink text-xl font-semibold">{items[2].t}</h3>
                <p className="text-ink/60 mt-2 text-sm leading-relaxed">{items[2].d}</p>
                <div className="mt-5 flex items-center gap-2 mono-num text-[11px]">
                  <span className="px-2 py-1 rounded-md hairline text-ink/70">BRONZE</span>
                  <span className="text-ink/30">→</span>
                  <span className="px-2 py-1 rounded-md hairline text-ink/70">PRATA</span>
                  <span className="text-ink/30">→</span>
                  <span className="px-2 py-1 rounded-md text-bg" style={{ background: "#FFB800" }}>OURO</span>
                </div>
              </div>
            </div>
          </motion.div>

          <FeatureCard idx={3} title={items[3].t} desc={items[3].d} Icon={ICONS[3]} accent="#00FF88" colSpan={2}>
            <div className="mt-5 flex items-center gap-3">
              <span className="mono-num text-3xl text-ink">+24</span>
              <span className="label-tag text-[10px]">DIAS</span>
              <div className="ml-auto h-2 w-2 rounded-full bg-primary animate-pulse-glow" />
            </div>
          </FeatureCard>

          <FeatureCard idx={4} title={items[4].t} desc={items[4].d} Icon={ICONS[4]} accent="#00FF88" colSpan={2}>
            <div className="mt-5 flex -space-x-2">
              {["A","B","C","D"].map((n, i) => (
                <div key={i} className="h-8 w-8 rounded-full hairline grid place-items-center mono-num text-[10px] text-ink"
                     style={{ background: i === 0 ? "rgba(0,255,136,0.15)" : "#262A33" }}>{n}</div>
              ))}
            </div>
          </FeatureCard>

          <FeatureCard idx={5} title={items[5].t} desc={items[5].d} Icon={ICONS[5]} accent="#00FF88" colSpan={2}>
            <div className="mt-5 flex flex-wrap gap-1.5">
              {["streak.csv","points.json","goals.csv","leagues.json"].map((f) => (
                <span key={f} className="mono-num text-[10px] px-2 py-1 rounded-md hairline text-ink/70">{f}</span>
              ))}
            </div>
          </FeatureCard>
        </div>
      </div>
    </section>
  );
}

function FeatureCard({ idx, title, desc, Icon, accent, colSpan = 2, children }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: idx * 0.04 }}
      className={`card-tactical p-6 md:p-7 col-span-1 md:col-span-${colSpan}`}
      style={{ gridColumn: `span ${colSpan}` }}
      data-testid={`feature-bento-${idx}`}
    >
      <div className="flex items-start gap-4">
        <div className="h-10 w-10 grid place-items-center rounded-md hairline flex-shrink-0"
             style={{ background: `${accent}10` }}>
          <Icon size={18} color={accent} />
        </div>
        <div>
          <h3 className="font-display text-ink text-xl font-semibold">{title}</h3>
          <p className="text-ink/60 mt-2 text-sm leading-relaxed">{desc}</p>
          {children}
        </div>
      </div>
    </motion.div>
  );
}
